import { FC, FormEvent, MouseEvent, ReactNode, useState } from 'react';
import { useNavigate } from 'libs/routing';
import { Button } from 'components/common/button';
import { useWagmi } from 'libs/wagmi';
import {
  addStrategyToCart,
  toGradientCartStorage,
} from 'components/cart/utils';
import config from 'config';
import { useStrategyFormCtx } from 'components/strategies/common/StrategyFormContext';
import { FormGradientOrder } from '../types';
import { carbonSDK } from 'libs/sdk';
import { Token } from 'libs/tokens';
import { parseUnits } from 'ethers';
import { useNotifications } from 'hooks/useNotifications';
import { useQueryClient } from '@tanstack/react-query';
import { QueryKey } from 'libs/queries';
import { createGradientStrategyParams } from './utils.sdk';
import { useCanBatchTransactions } from 'libs/queries/chain/canBatch';

interface FormProps {
  buy: FormGradientOrder;
  sell: FormGradientOrder;
  children: ReactNode;
}

export const CreateGradientStrategyForm: FC<FormProps> = (props) => {
  const { base, quote } = useStrategyFormCtx();
  const { children, buy, sell } = props;
  const { user, openConnect, sendTransaction } = useWagmi();
  const nav = useNavigate();
  const { dispatchNotification } = useNotifications();
  const cache = useQueryClient();

  const canBatch = useCanBatchTransactions();

  const [animating, setAnimating] = useState(false);
  const [loadingText, setLoadingText] = useState('');

  const isDisabled = (form: HTMLFormElement) => {
    if (!form.checkValidity()) return true;
    if (form.querySelector('.loading-message')) return true;
    if (form.querySelector('.error-message')) return true;
    const warnings = form.querySelector('.warning-message');
    if (!warnings) return false;
    return !form.querySelector<HTMLInputElement>('#approve-warnings')?.checked;
  };

  const addToCart = async (e: MouseEvent<HTMLButtonElement>) => {
    if (!user) return;
    const form = e.currentTarget.form!;
    if (!form.checkValidity()) return;
    if (form.querySelector('.loading-message')) return;
    if (form.querySelector('.error-message')) return;

    setAnimating(true);
    const params = toGradientCartStorage(base, quote, buy, sell);
    await addStrategyToCart(user, params);
    setAnimating(false);
    // Remove budget
    nav({
      to: '.',
      params: (params) => params,
      search: (s) => ({
        ...s,
        budget: undefined,
        sellBudget: undefined,
        buyBudget: undefined,
      }),
      replace: true,
      resetScroll: false,
    });
  };

  const create = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!user) return;
    if (isDisabled(e.currentTarget)) return;

    const strategy = { base, quote, buy, sell };
    const params = createGradientStrategyParams(strategy);
    const unsignedTx = await carbonSDK.createGradientStrategy(...params);
    const getRawAmount = (token: Token, amount: string) => {
      return parseUnits(amount || '0', token.decimals).toString();
    };
    unsignedTx.customData = {
      spender: config.addresses.carbon.gradientController,
      assets: [
        {
          address: base.address,
          rawAmount: getRawAmount(base, sell.budget),
        },
        {
          address: quote.address,
          rawAmount: getRawAmount(quote, buy.budget),
        },
      ],
    };
    const tx = await sendTransaction(unsignedTx);
    dispatchNotification('createStrategy', { txHash: tx.hash });
    await tx.wait();
    setTimeout(() => {
      const keys = [
        QueryKey.strategyAll(),
        QueryKey.balance(user, base.address),
        QueryKey.balance(user, quote.address),
      ];
      for (const queryKey of keys) {
        cache.invalidateQueries({ queryKey });
      }
    }, 3000);
    nav({ to: '/portfolio/strategies' });
  };

  const canBatchGradient = !!canBatch.data || !!config.addresses.carbon.router;

  return (
    <form
      onSubmit={create}
      className="form flex flex-1 flex-col gap-20"
      data-testid="create-strategy-form"
    >
      {children}
      <div className="surface approve-warnings rounded-lg bg-main-900/60 text-14 grid gap-16 p-20 text-main-0/60">
        <label
          htmlFor="approve-warnings"
          className="font-medium flex items-center gap-8"
        >
          <input
            id="approve-warnings"
            type="checkbox"
            className="size-18 shrink-0"
            data-testid="approve-warnings"
          />
          I accept any applicable warning(s) and understand fee on transfer
          (tax) or rebasing tokens are not supported
        </label>
      </div>

      {user && (
        <>
          {config.ui.showCart && canBatchGradient && (
            <Button
              className="add-cart btn-on-background shrink-0"
              type="button"
              size="lg"
              fullWidth
              disabled={!!loadingText || animating}
              onClick={addToCart}
              data-testid="add-strategy-to-cart"
            >
              Add to cart
            </Button>
          )}
          <Button
            className="shrink-0"
            type="submit"
            variant="success"
            size="lg"
            fullWidth
            loading={!!loadingText}
            loadingChildren={loadingText}
            data-testid="create-strategy"
          >
            Create strategy
          </Button>
        </>
      )}
      {!user && (
        <Button
          className="shrink-0"
          type="button"
          variant="success"
          size="lg"
          fullWidth
          loading={!!loadingText}
          loadingChildren={loadingText}
          onClick={openConnect}
        >
          Connect wallet
        </Button>
      )}
    </form>
  );
};
